import React, { forwardRef, useState, useEffect } from 'react';
import { formatClinicDate, formatClinicTime, clinicToday, toClinicDate, clinicNow } from '../utils/clinicTime';
import { prescriptionAPI, brandingAPI } from '../services/api';
import { URDU_PRINT_FONT } from '../utils/printDocument';
import { typePrefix } from './prescription/medicineLabel';
import pamhLogo from '../assets/pamh-logo.png';

// The sheet's colours are the PAMH logo's (the final mark, 2026-10-01): navy for the names and the
// section rules, green for the Urdu name and the item numbers, and the four figures' colours in the
// order they stand for the band under the letterhead. `teal` keeps its key from the first draft;
// it now holds the logo's green, deepened to #007F32 so the item numbers read as well as the text.
const BRAND = {
  navy: '#003078',
  teal: '#007F32',
  gold: '#C88800',
  sky: '#00A0C4',
};
const BAND_COLOURS = [BRAND.navy, BRAND.gold, '#009038', BRAND.sky];

// A strip of the four colours. Drawn with borders, not backgrounds: a browser printing without
// "Background graphics" drops background colours but always prints borders.
const BrandBand = ({ height }) => (
  <div style={{ display: 'flex', width: '100%' }}>
    {BAND_COLOURS.map((c) => (
      <div key={c} style={{ flex: '1 1 0', height: 0, borderTop: `${height}px solid ${c}` }} />
    ))}
  </div>
);


// Pair "; "-separated English special instructions with "؛ "-separated Urdu ones by index.
const pairInstructions = (en, ur) => {
  const ens = (en || '').split(/;\s*/).map(s => s.trim()).filter(Boolean);
  const urs = (ur || '').split(/؛\s*/).map(s => s.trim());
  return ens.map((e, i) => ({ en: e, ur: urs[i] || '' }));
};

// Short Urdu rendering of a duration (e.g. 3 + DAYS → "3 دن") tacked onto the Urdu dosage line.
// Numbers stay as Western digits even inside Urdu text (user rule).
// Units that name the course instead of counting it — no number, so the stored Urdu phrase is
// printed verbatim rather than run through urduDuration.
const UNCOUNTED_DURATION_UNITS = new Set(['UNTIL_FINISHED', 'CONTINUOUS']);

const urduDuration = (value, unit) => {
  const v = parseInt(value, 10);
  if (!v) return '';
  const u = String(unit || '').toUpperCase();
  const unitWord = u === 'WEEKS' ? (v === 1 ? 'ہفتہ' : 'ہفتے')
    : u === 'MONTHS' ? (v === 1 ? 'مہینہ' : 'مہینے')
    : 'دن'; // DAYS (and default)
  return `${v} ${unitWord}`;
};

/**
 * PrescriptionPrint Component
 * Two-column A4 prescription layout with Urdu support
 * Uses flexbox column layout so content flows naturally and
 * the footer is always pushed to the bottom of the page.
 *
 * Layout:
 *  ┌──────────────────────────────────────────┐
 *  │  Hospital Name / Urdu      Doctor Info   │
 *  ├──────────────────────────────────────────┤
 *  │  Patient | MRN | Age | Gender        Date │
 *  ├────────────┬─────────────────────────────┤
 *  │  Vitals    │                             │
 *  │  Diagnosis │   ℞ Medications             │
 *  │  Complaints│   (Urdu dosage)             │
 *  │  Clinical  │                             │
 *  │  Exam      │                             │
 *  │  Past labs │                             │
 *  │  Advised   │                             │
 *  ├────────────┴─────────────────────────────┤
 *  │  Instructions (Urdu)        │ Follow-up  │
 *  ├──────────────────────────────────────────┤
 *  │  Page n of m     no-signature notice(UR) │
 *  └──────────────────────────────────────────┘
 */
const PrescriptionPrint = forwardRef(({ prescription, patient, doctor, clinic }, ref) => {

  // Live instruction config for all three types, so the printout renders every selected instruction
  // in the order configured in Settings → Patient Instructions (not the order the doctor ticked
  // them). Patient-general selections are stored as configKeys (resolved to text here, covering
  // admin-added custom ones the hardcoded fallback map doesn't know); medication & lab instructions
  // are stored as resolved text, so they're ordered by matching that text to its displayOrder. Uses
  // the same endpoints the Rx form reads (any prescriber can load them); falls back gracefully.
  const [instrCfg, setInstrCfg] = useState(null);
  // Custom clinic logo (Settings → Branding) — replaces the built-in SVG in the header.
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    let alive = true;
    brandingAPI.get()
      .then(b => { if (alive) setBranding(b); })
      .catch(() => { if (alive) setBranding({ hasCustomLogo: false }); });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    let alive = true;
    Promise.all([
      prescriptionAPI.getConfig('PATIENT_INSTRUCTION').catch(() => []),
      prescriptionAPI.getConfig('MEDICATION_INSTRUCTION').catch(() => []),
      prescriptionAPI.getConfig('LAB_INSTRUCTION').catch(() => []),
    ]).then(([pat, med, lab]) => {
      if (!alive) return;
      // Instructions can be scoped to a speciality. Print keeps the unscoped fetch (so a key saved
      // by any doctor still resolves) but sorts this doctor's speciality items ahead of the general
      // ones, matching what they saw while writing. Tier is folded into the sort key.
      const doctorSpecId = doctor?.speciality?.id || doctor?.specialityId || null;
      const TIER_STRIDE = 100000;
      const rank = (c, i) => {
        const specId = c.speciality?.id || null;
        const tier = specId == null ? 1 : (specId === doctorSpecId ? 0 : 2);
        return tier * TIER_STRIDE + (c.displayOrder != null ? c.displayOrder : i);
      };
      const patientByKey = {};
      const patientOrderByKey = {};
      (Array.isArray(pat) ? pat : []).forEach((c, i) => {
        patientByKey[c.configKey] = { en: c.valueEnglish, ur: c.valueUrdu };
        patientOrderByKey[c.configKey] = rank(c, i);
      });
      const medOrderByText = {};
      const labOrderByText = {};
      (Array.isArray(med) ? med : []).forEach((c, i) => {
        medOrderByText[c.valueEnglish] = rank(c, i);
      });
      (Array.isArray(lab) ? lab : []).forEach((c, i) => {
        labOrderByText[c.valueEnglish] = rank(c, i);
      });
      setInstrCfg({ patientByKey, patientOrderByKey, medOrderByText, labOrderByText });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctor?.speciality?.id, doctor?.specialityId]);

  // Order a list of {en, ur} instruction pairs by their configured displayOrder (matched on English
  // text). Pairs not in the catalog keep their original relative position, after the ordered ones.
  const orderPairs = (pairs, orderByText) => {
    if (!orderByText || !Array.isArray(pairs)) return pairs || [];
    return pairs
      .map((p, i) => ({ p, i }))
      .sort((a, b) => {
        const oa = orderByText[a.p.en];
        const ob = orderByText[b.p.en];
        if (oa == null && ob == null) return a.i - b.i;
        if (oa == null) return 1;
        if (ob == null) return -1;
        return (oa - ob) || (a.i - b.i);
      })
      .map((x) => x.p);
  };

  // Letterhead identity, from Settings → Branding (it rides on the public branding response, which
  // this component already fetches for the logo). The `clinic` prop still wins if a caller passes
  // one. Only the name keeps a literal fallback, to cover the moment before the fetch lands;
  // address, phone and timings deliberately fall back to empty, because a blank line the admin can
  // see and fill is better than a plausible wrong address printed on a patient's prescription -
  // which is exactly what the old 'Main Road, City Name' placeholder did.
  const profile = branding?.profile;
  // A logo uploaded in Settings → Branding wins; otherwise the PAMH logo bundled with the app. The
  // letterhead and the watermark always show the same one.
  const logoSrc = branding?.hasCustomLogo ? brandingAPI.logoUrl(branding.logoVersion) : pamhLogo;
  const clinicInfo = {
    name: clinic?.name || profile?.nameEnglish || 'Perveen Aslam Memorial Hospital',
    nameUrdu: clinic?.nameUrdu || profile?.nameUrdu || 'پروین اسلم میموریل ہسپتال',
    address: clinic?.address ?? profile?.address ?? '',
    phone: clinic?.phone ?? profile?.phone ?? '',
    timings: clinic?.timings ?? profile?.timings ?? '',
  };
  // Each part drops out cleanly when unset, so an address-only or phone-only line still reads. It
  // prints small at the foot of the sheet (owner's choice, 2026-09-30), under the colour band, the
  // way a letterhead carries its address; the top of the sheet is left to the logo and the names.
  const clinicContactLine = [clinicInfo.address, clinicInfo.phone && `Tel: ${clinicInfo.phone}`, clinicInfo.timings]
    .filter(Boolean)
    .join(' | ');

  const formatDate = (date) => {
    const opts = { day: '2-digit', month: '2-digit', year: 'numeric' };
    if (!date) return formatClinicDate(clinicToday(), opts);
    return formatClinicDate(date, opts);
  };

  /**
   * The time of day the prescription was written, as 24 hour HH:MM beside the date (owner,
   * 4 Oct 2026). It comes from createdAt rather than prescriptionDate, which is a date with no time
   * in it at all; the two are stamped in the same moment when the prescription is started, so the
   * time can never belong to a different day from the date printed next to it. Nothing is shown for
   * a prescription that has not been saved yet and so has no timestamp.
   */
  const formatTimeOfDay = (value) =>
    value ? formatClinicTime(value, { hour: '2-digit', minute: '2-digit', hour12: false }, '') : '';

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = toClinicDate(dob);
    const today = clinicNow();
    if (!birthDate) return 'N/A';
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return `${age} Years`;
  };

  const instructionTexts = {
    AFTER_FOOD: { en: 'Take medicine after food', ur: 'دوائی کھانے کے بعد لیں' },
    BEFORE_FOOD: { en: 'Take medicine before food', ur: 'دوائی کھانے سے پہلے لیں' },
    EMPTY_STOMACH: { en: 'Take on empty stomach', ur: 'خالی پیٹ لیں' },
    WITH_WATER: { en: 'Take with plenty of water', ur: 'پانی کے ساتھ لیں' },
    NO_DRIVING: { en: 'Avoid driving after taking medicine', ur: 'دوائی لینے کے بعد گاڑی نہ چلائیں' },
    AVOID_SUN: { en: 'Avoid direct sunlight', ur: 'دھوپ سے بچیں' },
    NO_ALCOHOL: { en: 'Avoid alcohol', ur: 'شراب سے پرہیز کریں' },
    COMPLETE_COURSE: { en: 'Complete the full course of medicine', ur: 'دوائی کا پورا کورس مکمل کریں' },
    REST: { en: 'Take proper rest', ur: 'مکمل آرام کریں' },
    DRINK_FLUIDS: { en: 'Drink plenty of fluids', ur: 'زیادہ پانی پئیں' },
    AVOID_OILY: { en: 'Avoid oily/spicy food', ur: 'تلی ہوئی اور مصالحے دار چیزوں سے پرہیز کریں' },
    LIGHT_DIET: { en: 'Take light diet', ur: 'ہلکی غذا لیں' },
    NO_SMOKING: { en: 'Avoid smoking', ur: 'سگریٹ نوشی سے پرہیز کریں' },
  };

  let parsedInstructions = [];
  try {
    if (prescription?.patientInstructions) {
      const raw = typeof prescription.patientInstructions === 'string'
        ? JSON.parse(prescription.patientInstructions)
        : prescription.patientInstructions;
      if (Array.isArray(raw)) parsedInstructions = raw;
    }
  } catch (e) { /* ignore */ }

  // Resolve an instruction key to {en, ur}: prefer the live config (covers admin-added custom
  // instructions), fall back to the built-in hardcoded map for legacy keys not in the active config.
  const resolveInstruction = (key) => (instrCfg?.patientByKey?.[key]) || instructionTexts[key] || null;
  // Selected instructions ordered by the configured displayOrder (Settings → Patient Instructions);
  // keys with no known order keep their original relative position, after the ordered ones.
  const orderedInstructions = parsedInstructions
    .map((key, i) => ({ key, i }))
    .sort((a, b) => {
      const oa = instrCfg?.patientOrderByKey?.[a.key];
      const ob = instrCfg?.patientOrderByKey?.[b.key];
      if (oa == null && ob == null) return a.i - b.i;
      if (oa == null) return 1;
      if (ob == null) return -1;
      return (oa - ob) || (a.i - b.i);
    })
    .map((x) => x.key);

  let parsedComplaints = [];
  try {
    if (prescription?.complaints) {
      const raw = typeof prescription.complaints === 'string'
        ? JSON.parse(prescription.complaints)
        : prescription.complaints;
      if (Array.isArray(raw)) parsedComplaints = raw;
    }
  } catch (e) { /* ignore */ }
  // Order complaints by duration (longest-standing first) to match the prescription screen.
  const SINCE_UNIT_DAYS = { day: 1, days: 1, week: 7, weeks: 7, month: 30, months: 30, year: 365, years: 365 };
  const sinceToDays = (since) => {
    const m = String(since || '').trim().match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/);
    return m ? (parseFloat(m[1]) || 0) * (SINCE_UNIT_DAYS[m[2].toLowerCase()] || 1) : -1;
  };
  parsedComplaints = parsedComplaints
    .map((c, i) => ({ c, i }))
    .sort((a, b) => (sinceToDays(b.c.since) - sinceToDays(a.c.since)) || (a.i - b.i))
    .map(x => x.c);

  const hasInstructions = parsedInstructions.length > 0;
  const hasFollowup = prescription?.followupDays || prescription?.followupDate;
  const hasBottomSection = hasInstructions || hasFollowup;

  const urduFont = URDU_PRINT_FONT;
  // Every line of Urdu on the sheet is set one way: the follow-up line's size, weight and colour,
  // the look the hospital picked (2026-09-13). Each line used to carry its own size, 13.5px to 19px,
  // and in Nastaliq the larger, darker ones read as a heavier typeface than the rest. The letterhead
  // name alone keeps its own size and brand green, as the heading beside the English name.
  // lineHeight is explicit because the preview inherits 1.5 from the app's base styles while the
  // print window inherits nothing, and Nastaliq's default line box is far taller, so the paper was
  // spaced differently from the screen.
  const urduText = {
    fontFamily: urduFont,
    direction: 'rtl',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: 1.5,
    color: '#374151',
  };

  // Where a line exists in both languages the printed slip carries only the Urdu, the language the
  // patient reads, and the English prints only where there is no Urdu to show instead (2026-09-13).
  // Printing both doubled every line of the medicines, tests and instructions. English that has no
  // Urdu counterpart (medicine names, headings, the clinical notes) is untouched.
  //
  // One instruction line under that rule: the Urdu alone, right-aligned as Urdu reads, or the English
  // with its bullet for an instruction saved without a translation.
  const instructionLine = (pair, key, rowStyle) => {
    const ur = (pair.ur || '').trim();
    return (
      <div key={key} style={{ ...rowStyle, justifyContent: ur ? 'flex-end' : 'flex-start' }}>
        {ur
          ? <span style={{ ...urduText, textAlign: 'right', minWidth: 0 }}>{ur}</span>
          : <span style={{ minWidth: 0, overflowWrap: 'break-word' }}>&bull; {pair.en}</span>}
      </div>
    );
  };
  // The row under a medicine or a test that carries its special instructions.
  const subInstructionRow = { display: 'flex', alignItems: 'baseline', gap: '12px', fontSize: '12.5px', color: '#374151' };

  const sidebarLabel = {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 3px 0',
  };

  const sidebarValue = {
    fontSize: '12px',
    margin: 0,
    color: '#1f2937',
    lineHeight: '1.4',
    // The doctor types these as free text, so paragraphs and line breaks are meaningful. HTML
    // collapses newlines into spaces by default, which ran a multi-line examination or clinical
    // note together on one line. pre-wrap keeps the breaks while still wrapping long lines to the
    // column width; break-word stops an unbroken long token overflowing the narrow sidebar.
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
  };

  return (
    <div ref={ref} className="prescription-print bg-white" style={{
      width: '210mm',
      // 296 rather than 297: at exactly one page height, sub-pixel rounding in the print pipeline
      // is enough to spill and emit a blank second sheet.
      minHeight: '296mm',
      // Generous enough to clear the non-printable edge every consumer printer has. The old 8/10mm
      // became 5mm in print, which put the right-hand Urdu column right on the paper edge where it
      // was cropped.
      padding: '12mm 14mm',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      color: '#1f2937',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      // The watermark sits behind the text: position + isolation make the sheet its own stacking
      // context, so the watermark's negative z-index puts it above the white paper but below
      // every line of content. Inline, so each page the paginator clones keeps it.
      position: 'relative',
      isolation: 'isolate',
    }}>

      {/* ========== WATERMARK ==========
          The logo, faint and centred on the sheet. Absolutely positioned, so it takes no room in
          the column and never moves the content. paginatePrintSheet copies it onto every page. */}
      <img
        data-rx-watermark
        src={logoSrc}
        alt=""
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '130mm',
          height: '130mm',
          objectFit: 'contain',
          transform: 'translate(-50%, -50%)',
          opacity: 0.07,
          zIndex: -1,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />

      {/* ========== HEADER ========== */}
      <div data-rx-header style={{ flexShrink: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '8px' }}>
        {/* The logo hangs from the top of the name block rather than centring on it. Centring looked
            off because the Urdu name is set in Nastaliq, whose line box carries far more space above
            and below the visible strokes than the Latin lines do: the box centre sits well below the
            centre of the text you actually see, and the logo drifted down with it. Aligning the
            logo's cap to the hospital name's cap is a fixed reference that does not move with the
            script. */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0 }}>
          <img
            src={logoSrc}
            alt="Hospital logo"
            style={{ width: '72px', height: '72px', objectFit: 'contain', flexShrink: 0 }}
          />
          <div>
            <h1 style={{ fontSize: '21px', fontWeight: 'bold', color: BRAND.navy, margin: '4px 0 0 0', letterSpacing: '0.2px' }}>
              {clinicInfo.name}
            </h1>
            <p style={{ fontSize: '18px', fontFamily: urduFont, direction: 'rtl', lineHeight: 1.5, margin: '2px 0 0 0', color: BRAND.teal }}>
              {clinicInfo.nameUrdu}
            </p>
          </div>
          </div>
        </div>
        <div style={{ textAlign: 'right', minWidth: 0, flexShrink: 0 }}>
          <p style={{ fontWeight: 'bold', fontSize: '15px', margin: '4px 0 0 0', color: BRAND.navy }}>
            {doctor?.fullName
              || ((doctor?.firstName || doctor?.lastName)
                ? `Dr. ${doctor?.firstName || ''} ${doctor?.lastName || ''}`.trim()
                : 'Doctor')}
          </p>
          <p style={{ fontSize: '11px', color: '#6b7280', margin: '2px 0' }}>
            {doctor?.specialization || 'General Physician'}
          </p>
          {doctor?.qualification && (
            <p style={{ fontSize: '11px', color: '#6b7280', margin: '2px 0' }}>
              {doctor.qualification}
            </p>
          )}
          {/* The doctor's clinic timings (Doctor Settings, 2026-10-01), so the patient knows when to
              come back: one line per run of days, in English only (owner, 2026-10-01: the Urdu line
              was removed). The server writes the line (util/DoctorTimings), so the slip, the token
              and the Display Board say the same. */}
          {doctor?.workingHoursText && (
            <div data-rx-doctor-timings style={{ marginTop: '4px', maxWidth: '70mm', marginLeft: 'auto' }}>
              {doctor.workingHoursText.split(' · ').map((line) => (
                <p key={line} style={{ fontSize: '10.5px', color: BRAND.navy, margin: '1px 0', fontWeight: 600 }}>
                  {line}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* The four-colour band that used to sit here was removed on the owner's instruction
          (4 Oct 2026). The patient bar below carries its own rule, so the header is still closed
          off; the band survives in the footer, above the address. */}
      </div>

      {/* ========== PATIENT INFO BAR ========== */}
      <div data-rx-patient style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        padding: '8px 0',
        borderBottom: '1px solid #d1d5db',
        // The values inherit this. It is the line a pharmacist and the patient check first, so it
        // is set larger than the body text rather than smaller.
        fontSize: '14px',
        flexShrink: 0,
      }}>
        {/* One evenly spread row rather than a left cluster and a lone right field. Dropping Rx#
            left the date marooned against the right edge with a canyon of white beside it. */}
        <div>
          <span style={{ color: '#6b7280', fontSize: '10.5px', textTransform: 'uppercase' }}>Patient</span>
          <p style={{ fontWeight: 'bold', margin: '1px 0 0 0' }}>{patient?.firstName} {patient?.lastName}</p>
        </div>
        <div>
          <span style={{ color: '#6b7280', fontSize: '10.5px', textTransform: 'uppercase' }}>MR#</span>
          <p style={{ fontWeight: 'bold', margin: '1px 0 0 0' }}>{patient?.mrn}</p>
        </div>
        <div>
          <span style={{ color: '#6b7280', fontSize: '10.5px', textTransform: 'uppercase' }}>Age/Gender</span>
          <p style={{ fontWeight: 'bold', margin: '1px 0 0 0' }}>{patient?.dobEstimated ? 'About ' : ''}{calculateAge(patient?.dateOfBirth)} / {patient?.gender}</p>
        </div>
        {/* Rx number is deliberately not printed: it is an internal reference the patient and the
            pharmacist never need, and it crowded the line that carries the details they do read. */}
        <div style={{ textAlign: 'right' }}>
          <span style={{ color: '#6b7280', fontSize: '10.5px', textTransform: 'uppercase' }}>Date</span>
          <p style={{ fontWeight: 'bold', margin: '1px 0 0 0' }}>
            {formatDate(prescription?.prescriptionDate)}
            {/* Lighter than the date so the date still leads the field. */}
            {formatTimeOfDay(prescription?.createdAt) && (
              <span style={{ fontWeight: 400, marginLeft: '6px' }}>
                {formatTimeOfDay(prescription.createdAt)}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* ========== TWO-COLUMN BODY (sized to its content) ==========
          Deliberately NOT flex-grow: stretching this to fill the page dragged the instructions
          block down to the paper's edge and left a canyon of white between the medicines and the
          advice, with the column divider running through the middle of it. Content height keeps
          the sheet reading top-down; only the footer is pinned to the bottom. The min-height stops
          a one-line prescription from looking cramped under the letterhead. */}
      <div data-rx-body style={{ display: 'flex', flex: '0 0 auto', minHeight: '55mm' }}>

        {/* --- LEFT SIDEBAR (36%) - the clinical picture that led to the prescription --- */}
        <div data-rx-sidebar style={{
          width: '36%',
          borderRight: '1px solid #d1d5db',
          paddingRight: '12px',
          paddingTop: '10px',
        }}>
          {/* Vitals */}
          {(prescription?.bpSystolic || prescription?.pulse || prescription?.temperature || prescription?.weight || prescription?.bloodSugar) && (
            <div style={{ marginBottom: '14px' }}>
              <p style={sidebarLabel}>Vitals</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {prescription?.bpSystolic && prescription?.bpDiastolic && (
                  <p style={sidebarValue}><strong>BP:</strong> {prescription.bpSystolic}/{prescription.bpDiastolic} mmHg</p>
                )}
                {prescription?.pulse && (
                  <p style={sidebarValue}><strong>Pulse:</strong> {prescription.pulse} BPM</p>
                )}
                {prescription?.temperature && (
                  <p style={sidebarValue}><strong>Temp:</strong> {prescription.temperature}°F</p>
                )}
                {prescription?.weight && (
                  <p style={sidebarValue}><strong>Weight:</strong> {prescription.weight} KG</p>
                )}
                {prescription?.bloodSugar && (
                  <p style={sidebarValue}><strong>Sugar:</strong> {prescription.bloodSugar} mg/dL</p>
                )}
                {prescription?.spo2 && (
                  <p style={sidebarValue}><strong>SpO2:</strong> {prescription.spo2}%</p>
                )}
              </div>
            </div>
          )}

          {/* Diagnosis — printed above the complaints, which read as its supporting detail */}
          {prescription?.diagnosis && (
            <div style={{ marginBottom: '14px' }}>
              {/* Wording tracks the writer's own field label (DiagnosisSection), so the printed
                  slip says the same thing the doctor filled in.
                  Section headings are English-only by design; the Urdu on this sheet is reserved
                  for content the patient actually reads (dosage and instruction translations). */}
              <p style={sidebarLabel}>Diagnosis/Provisional Diagnosis</p>
              <p style={sidebarValue}>{prescription.diagnosis}</p>
            </div>
          )}

          {/* Chief Complaints */}
          {parsedComplaints.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <p style={sidebarLabel}>Chief Complaints</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {parsedComplaints.map((c, idx) => (
                  <p key={idx} style={sidebarValue}>
                    {c.complaint}
                    {c.since ? <span style={{ color: '#6b7280' }}> - {c.since}</span> : null}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Clinical Notes */}
          {prescription?.clinicalNotes && (
            <div style={{ marginBottom: '14px' }}>
              <p style={sidebarLabel}>Clinical Notes</p>
              <p style={sidebarValue}>{prescription.clinicalNotes}</p>
            </div>
          )}

          {/* Examination */}
          {prescription?.examination && (
            <div style={{ marginBottom: '14px' }}>
              <p style={sidebarLabel}>Examination</p>
              <p style={sidebarValue}>{prescription.examination}</p>
            </div>
          )}

          {/* Past Lab Reports — the doctor's read of the reports the patient brought to THIS
              visit, so the printed copy carries the same record the file does. The tests being
              ORDERED are not here: they moved next to the medicines, which is where the patient
              looks for what to do next. */}
          {prescription?.pastLabReports && (
            <div style={{ marginBottom: '14px' }}>
              <p style={sidebarLabel}>Past Lab Reports</p>
              <p style={sidebarValue}>{prescription.pastLabReports}</p>
            </div>
          )}

        </div>

        {/* --- RIGHT MAIN AREA (64%) - what was prescribed: medicines, then lab tests --- */}
        <div data-rx-flow style={{
          width: '64%',
          paddingLeft: '14px',
          paddingTop: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', paddingBottom: '5px', borderBottom: `2px solid ${BRAND.navy}` }}>
            <span style={{ fontSize: '28px', fontWeight: 'bold', color: BRAND.navy, marginRight: '8px', lineHeight: 1 }}>&#8478;</span>
            <span style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '17px' }}>Medications</span>
          </div>

          {prescription?.medicines?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {prescription.medicines.map((med, idx) => {
                const instrs = med.specialInstructions
                  ? orderPairs(pairInstructions(med.specialInstructions, med.specialInstructionsUrdu), instrCfg?.medOrderByText)
                  : [];
                // Backend rows carry the duration separately in durationUrdu; local/template rows
                // already embed it inside dosageInstructionUrdu, so appending there would double it.
                // "Until Finished" / "Continuous" have no number to render, so the stored phrase is
                // used as-is; urduDuration would return "" for them and silently drop the course.
                const durationUrduText = !med.durationUrdu
                  ? ''
                  : UNCOUNTED_DURATION_UNITS.has(String(med.durationUnit || '').toUpperCase())
                    ? med.durationUrdu
                    : urduDuration(med.durationValue, med.durationUnit);
                const urduLine = [med.dosageInstructionUrdu, durationUrduText].filter(Boolean).join(' - ');
                // The Urdu dosage stands in for the English one. A line saved before Urdu was
                // generated has only the English, which then prints as it always did.
                const showUrduDosage = !!med.dosageInstructionUrdu || (!med.dosageInstructionEnglish && !!urduLine);
                return (
                  <div key={med.id || idx} style={{ paddingBottom: '12px', borderBottom: '1px dashed #d1d5db' }}>
                    {/* Medicine name. No duration badge in the corner: the course length already
                        closes both dosage lines below ("... for 7 days" / "... 7 دن"), and a third
                        copy of it up here only competed with the medicine name. */}
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#111827' }}>
                      <span style={{ color: BRAND.teal, marginRight: '4px' }}>{idx + 1}.</span>
                      {[typePrefix(med.medicineType, med.medicineName), med.medicineName].filter(Boolean).join(' ')}
                      {med.strength && <span style={{ color: '#6b7280', fontWeight: 'normal', fontSize: '13px', marginLeft: '5px' }}>{med.strength}</span>}
                    </div>
                    {/* Urdu dosage — full-width RTL line of its own so it never wraps awkwardly.
                        The duration closes this line (so it lands at the left, RTL) instead of
                        sitting up in the header badge, matching the on-screen prescription view.
                        The English dosage prints only for a line that has no Urdu. */}
                    {showUrduDosage ? (
                      <div style={{ ...urduText, textAlign: 'right', marginTop: '2px' }}>
                        {urduLine}
                      </div>
                    ) : med.dosageInstructionEnglish ? (
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151', marginTop: '4px', paddingLeft: '17px', overflowWrap: 'break-word' }}>
                        {med.dosageInstructionEnglish}
                      </div>
                    ) : null}
                    {/* An injection or drip given here one dose per visit: the patient has to come back
                        for each dose, which the dosage line alone does not say. */}
                    {med.givenAtClinic === true && (
                      <div data-rx-given-here style={{ ...urduText, textAlign: 'right', marginTop: '2px' }}>
                        کلینک آ کر لگوائیں
                      </div>
                    )}
                    {/* Special instructions — one per line, in Urdu where translated */}
                    {instrs.length > 0 && (
                      <div style={{ marginTop: '4px', paddingLeft: '17px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {instrs.map((p, i) => instructionLine(p, i, subInstructionRow))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ color: '#9ca3af', fontStyle: 'italic', padding: '10px', fontSize: '12px' }}>No medications prescribed</p>
          )}

          {/* Advised Lab Tests — under the medicines rather than in the sidebar, because both are
              things being PRESCRIBED and the patient reads them as one list of what to do next.
              The sidebar keeps the clinical picture that led to them (vitals, complaints, past
              reports).
              The wider column also gives a test's Urdu name room on its own line. The old sidebar
              version had to stack the two scripts because 36% is too narrow to carry both, which is
              what made the Urdu look cropped. */}
          {prescription?.labTests?.length > 0 && (
            <div style={{ marginTop: prescription?.medicines?.length > 0 ? '14px' : '10px' }}>
              {/* Same header treatment as Medications, minus the R glyph, which belongs to the
                  medicines alone. A peer section, so it keeps the 2px rule. */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                paddingBottom: '5px',
                borderBottom: `2px solid ${BRAND.navy}`,
              }}>
                <span style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '17px' }}>Advised Lab Tests</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {prescription.labTests.map((test, idx) => {
                  const labInstr = test.instructions
                    ? orderPairs(pairInstructions(test.instructions, test.instructionsUrdu), instrCfg?.labOrderByText)
                    : [];
                  const last = idx === prescription.labTests.length - 1;
                  return (
                    <div key={test.id || idx} style={{
                      paddingBottom: last ? 0 : '8px',
                      borderBottom: last ? 'none' : '1px dashed #d1d5db',
                    }}>
                      {/* Numbered and teal like a medicine, so the two lists read as one voice.
                          A test prints in BOTH languages, unlike the instructions: the English name
                          leads at the left, as a medicine's does, because that is the name the lab
                          works from; the Urdu name closes the same line at the right, where an Urdu
                          reader starts. A pair too long for one line wraps with the Urdu still
                          right-aligned. A test with only one of the names prints that one. */}
                      {test.testName || !test.testNameUrdu ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: '10px' }}>
                          <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#111827', overflowWrap: 'break-word', minWidth: 0 }}>
                            <span style={{ color: BRAND.teal, marginRight: '4px' }}>{idx + 1}.</span>
                            {test.testName}
                          </div>
                          {test.testNameUrdu && (
                            <span style={{ ...urduText, textAlign: 'right', marginLeft: 'auto', minWidth: 0 }}>
                              {test.testNameUrdu}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: '6px' }}>
                          <span style={{ ...urduText, textAlign: 'right', minWidth: 0 }}>
                            {test.testNameUrdu}
                          </span>
                          <span style={{ fontSize: '15px', fontWeight: 'bold', color: BRAND.teal, flexShrink: 0 }}>{idx + 1}.</span>
                        </div>
                      )}
                      {labInstr.length > 0 && (
                        <div style={{ marginTop: '4px', paddingLeft: '17px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {labInstr.map((pr, i) => instructionLine(pr, i, subInstructionRow))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========== BOTTOM SECTION: Instructions + Follow-up ========== */}
      {hasBottomSection && (
        <div data-rx-tail style={{
          borderTop: '1px solid #d1d5db',
          marginTop: '14px',
          paddingTop: '8px',
          display: 'flex',
          gap: '12px',
          flexShrink: 0,
        }}>
          {hasInstructions && (
            <div style={{ flex: hasFollowup ? '1 1 70%' : '1 1 100%' }}>
              <p style={{ ...sidebarLabel, marginBottom: '4px' }}>Instructions</p>
              <div style={{ fontSize: '11.5px' }}>
                {orderedInstructions.map((key, idx) => {
                  const inst = resolveInstruction(key);
                  if (!inst) return null;
                  return instructionLine(inst, key, {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '2px 0',
                    borderBottom: idx < orderedInstructions.length - 1 ? '1px dashed #e5e7eb' : 'none',
                  });
                })}
              </div>
            </div>
          )}

          {hasFollowup && (
            <div style={{
              flex: hasInstructions ? '0 0 28%' : '1 1 100%',
              borderLeft: hasInstructions ? '1px solid #d1d5db' : 'none',
              paddingLeft: hasInstructions ? '12px' : '0',
            }}>
              <p style={{ ...sidebarLabel, marginBottom: '4px' }}>Follow-up</p>
              {/* The Urdu is built from the day count alone, so it replaces "After N days" but not a
                  follow-up date, which it never mentions: a date still prints, beneath it. */}
              {prescription.followupInstructionUrdu ? (
                <>
                  <p style={{ ...urduText, margin: 0 }}>
                    {prescription.followupInstructionUrdu}
                  </p>
                  {prescription.followupDate && (
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', textAlign: 'right' }}>
                      {formatDate(prescription.followupDate)}
                    </p>
                  )}
                </>
              ) : (
                <p style={{ margin: 0, fontSize: '12px' }}>
                  {prescription.followupDays && `After ${prescription.followupDays} days`}
                  {prescription.followupDate && ` (${formatDate(prescription.followupDate)})`}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========== FOOTER LINE (pushed to bottom via flex) ========== */}
      {/* Right-hand side carries the no-signature notice, which replaced both the old
          sign-and-stamp box and the "Get well soon" line. marginTop:auto pins this to the page
          bottom whatever the prescription's length. */}
      <div data-rx-footer style={{ marginTop: 'auto', flexShrink: 0 }}>
      <BrandBand height={2} />
      {/* The hospital's address, phone and timings first, centred on a line of their own, then the
          printed-at line and the no-signature notice at the very bottom (owner, 2026-10-01). The
          address used to sit under the notice and the Nastaliq line box ran into it. */}
      {clinicContactLine && (
        <p data-rx-address style={{ fontSize: '12px', color: '#4b5563', margin: '6px 0 0 0', textAlign: 'center', overflowWrap: 'break-word' }}>
          {clinicContactLine}
        </p>
      )}
      <div style={{
        paddingTop: clinicContactLine ? '8px' : '6px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        fontSize: '10px',
        color: '#6b7280',
        flexShrink: 0,
      }}>
        {/* The printed-at timestamp was removed on the owner's instruction (4 Oct 2026): the slip
            is a clinical record handed to a patient, and the moment it came off the printer is of
            no use to them. The page number stays, filled in only when a long slip actually runs to
            more than one sheet.

            Nastaliq carries a much taller line box than Arial, so both sides get the same explicit
            line-height, otherwise the two sides read as separate lines rather than one footer. */}
        <p style={{ margin: 0, flexShrink: 0, lineHeight: '22px' }}>
          <span data-rx-pageno />
        </p>
        {/* Smaller than the body Urdu (owner, 4 Oct 2026): it is a disclaimer, not something the
            patient has to read, and at 16px it carried more weight on the page than the address
            above it. 22px of line box rather than the 1.5 ratio, because Nastaliq's descenders
            clip in a tight line and this is the last line on the sheet. */}
        <p style={{ ...urduText, fontSize: '13px', lineHeight: '22px', margin: 0, textAlign: 'right' }}>
          یہ کمپیوٹر سے پرنٹ شدہ نسخہ ہے، اس پر دستخط کی ضرورت نہیں
        </p>
      </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          /* The sheet keeps the exact geometry it has on screen. It used to be forced to
             width:100% and padding:5mm here, which is what pushed content into the paper edge. */
          .prescription-print {
            width: 210mm !important;
            min-height: 296mm !important;
            padding: 12mm 14mm !important;
            overflow: hidden;
          }
          @page {
            size: A4;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        @font-face {
          font-family: 'Jameel Noori Nastaleeq';
          src: local('Jameel Noori Nastaleeq'), local('Noto Nastaliq Urdu');
        }
      `}</style>
    </div>
  );
});

PrescriptionPrint.displayName = 'PrescriptionPrint';

export default PrescriptionPrint;
